import type { ReactNode } from "react";
import { useEffect, useRef } from "react";

import { usePostCacheContext } from "@/contexts/post-cache-context";
import { usePostContext } from "@/contexts/post-context";
import { feedFreezeDebugLog } from "@/utils/feed-freeze-debug";

type PostObserverProps = {
  children: ReactNode;
  initialIsIntersecting?: boolean;
};

// Smallest possible height on mobile
const DEFAULT_HEIGHT = 174;

export const PostObserver = ({
  children,
  initialIsIntersecting,
}: PostObserverProps) => {
  const intersectionRef = useRef<HTMLDivElement>(null!);
  const contentRef = useRef<HTMLDivElement>(null!);

  const { postHash, postCachePrefix } = usePostContext();

  const {
    loadIntersectionObserver,
    resizeObserver,
    loadIntersecting = initialIsIntersecting,
    wrapperHeight = DEFAULT_HEIGHT,
    postCacheKey,
    fixed,
  } = usePostCacheContext(postHash, postCachePrefix);

  useEffect(() => {
    const intersectionNode = intersectionRef.current;
    const resizeNode = contentRef.current;

    feedFreezeDebugLog("post-observer", "mount", {
      initialIsIntersecting: initialIsIntersecting ?? false,
      postCacheKey,
      postHash,
    });

    loadIntersectionObserver.observe(intersectionNode);
    resizeObserver.observe(resizeNode);

    return () => {
      loadIntersectionObserver.unobserve(intersectionNode);
      resizeObserver.unobserve(resizeNode);
      feedFreezeDebugLog("post-observer", "unmount", {
        postCacheKey,
        postHash,
      });
    };
  }, []);

  useEffect(() => {
    feedFreezeDebugLog(
      "post-observer",
      "state",
      {
        fixed: !!fixed,
        loadIntersecting: !!loadIntersecting,
        postCacheKey,
        postHash,
        wrapperHeight,
      },
      {
        throttleKey: `post-observer-state:${postCacheKey}`,
        throttleMs: 250,
      },
    );
  }, [fixed, loadIntersecting, postCacheKey, postHash, wrapperHeight]);

  const shouldRenderChildren = loadIntersecting || fixed;

  return (
    <div
      className="min-h-min"
      style={{
        height:
          initialIsIntersecting && wrapperHeight === DEFAULT_HEIGHT
            ? "auto"
            : wrapperHeight,
      }}
      ref={intersectionRef}
      data-cache-key={postCacheKey}
    >
      <div
        className="empty-none min-h-min"
        ref={contentRef}
        data-cache-key={postCacheKey}
        data-track="wrapper"
      >
        {shouldRenderChildren && children}
      </div>
    </div>
  );
};
